/**
 * KotH session keys — owner-authorized ephemeral keypair lifecycle.
 *
 * Flow: owner clicks "Enable rapid-fire" → we generate a fresh keypair, fund
 * it from the owner via SystemTransfer (~10 touches' worth + last_touch rent),
 * call `koth_delegate_session_key` to authorize it on chain, then persist the
 * secret. Subsequent `koth_touch_delegated` calls sign with the cached keypair
 * — no wallet popup.
 *
 * Storage: IndexedDB primary (origin-scoped object store), localStorage
 * fallback for old browsers / private-mode contexts that block IDB. A
 * hydrated in-memory cache fronts both so `getActiveSessionKey` can stay
 * synchronous — `subscribeSessionKeyChange` notifies after hydration so
 * consumers can re-read.
 *
 * The cached secret is bearer: anyone who reads the store can spend the
 * pre-funded budget until on-chain `expires_at` (≤ 1h, enforced by the program
 * via `MAX_SESSION_KEY_SECS`). We clear the entry as soon as expiry passes.
 */
import {
  AnchorProvider,
  BN,
  Program,
  type Idl as AnchorIdl,
} from "@coral-xyz/anchor";
import type { WalletContextState } from "@solana/wallet-adapter-react";
import {
  Connection,
  Keypair,
  PublicKey,
  SystemProgram,
  Transaction,
  type VersionedTransaction,
} from "@solana/web3.js";
import { IDL, PROGRAM_ID } from "@solblox/shared";

import { buildWalletProgram, getProgramId } from "../games/last-touch/anchor";

const LS_PREFIX = "solblox.session_key.";
const IDB_NAME = "solblox.session_keys";
const IDB_STORE = "entries";
const IDB_VERSION = 1;
const DEFAULT_DURATION_SECS = 15 * 60;
// Sized for ~20 touches over the 15-min window. Touch fees scale
// `base + step * touch_count` (1M + 100k * n lamports) and accrue *to* the
// round pot, not lost — so an aggressive top-up is cheap user-side. Also
// covers tx-fee burn (5k/sig) + the rent-exempt floor (~890k for a 0-data
// system account) that the runtime enforces post-tx.
const TOP_UP_LAMPORTS = 30_000_000;
const EXPIRY_GRACE_SECS = 15;

interface StoredSessionKey {
  ownerBase58: string;
  delegateSecretBase64: string;
  expiresAt: number;
  delegateBase58: string;
}

export interface ActiveSessionKey {
  keypair: Keypair;
  sessionKeyPda: PublicKey;
  expiresAt: number;
}

interface MethodsBuilder {
  accounts(accounts: Record<string, unknown>): MethodsBuilder;
  signers(signers: Keypair[]): MethodsBuilder;
  rpc(): Promise<string>;
  instruction(): Promise<unknown>;
}

interface KothMethods {
  kothDelegateSessionKey(delegate: PublicKey, durationSecs: BN): MethodsBuilder;
  kothRevokeSessionKey(): MethodsBuilder;
  kothTouchDelegated(): MethodsBuilder;
}

export function deriveSessionKeyPda(
  owner: PublicKey,
  programId?: PublicKey,
): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from("session_key"), owner.toBuffer()],
    programId ?? getProgramId(),
  );
  return pda;
}

function lsKey(ownerBase58: string): string {
  return LS_PREFIX + ownerBase58;
}

// --- IndexedDB layer (best-effort; resolves to null on failure) -------------

function openIdb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(IDB_NAME, IDB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE, { keyPath: "ownerBase58" });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGetAll(): Promise<StoredSessionKey[]> {
  const db = await openIdb();
  if (!db) return [];
  return new Promise((resolve) => {
    let req: IDBRequest;
    try {
      req = db.transaction(IDB_STORE, "readonly").objectStore(IDB_STORE).getAll();
    } catch {
      resolve([]);
      return;
    }
    req.onerror = () => resolve([]);
    req.onsuccess = () => resolve((req.result as StoredSessionKey[]) ?? []);
  });
}

async function idbPut(entry: StoredSessionKey): Promise<void> {
  const db = await openIdb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put(entry);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

async function idbDelete(ownerBase58: string): Promise<void> {
  const db = await openIdb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).delete(ownerBase58);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

// --- In-memory cache + change notifications --------------------------------

const memoryCache = new Map<string, StoredSessionKey>();
const changeListeners = new Set<() => void>();
let hydrated = false;
let hydratePromise: Promise<void> | null = null;

function notifyChange(): void {
  for (const cb of changeListeners) cb();
}

export function subscribeSessionKeyChange(cb: () => void): () => void {
  changeListeners.add(cb);
  return () => {
    changeListeners.delete(cb);
  };
}

async function hydrate(): Promise<void> {
  const idbEntries = await idbGetAll();
  for (const entry of idbEntries) {
    memoryCache.set(entry.ownerBase58, entry);
  }
  // Promote any localStorage-only entries (old format / IDB-blocked browsers)
  // into the cache + IDB so future reads converge on IDB.
  if (typeof localStorage !== "undefined") {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k?.startsWith(LS_PREFIX)) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as StoredSessionKey;
        if (!memoryCache.has(parsed.ownerBase58)) {
          memoryCache.set(parsed.ownerBase58, parsed);
          void idbPut(parsed);
        }
      } catch {
        /* skip malformed */
      }
    }
  }
  hydrated = true;
  notifyChange();
}

export function whenSessionKeysHydrated(): Promise<void> {
  if (hydrated) return Promise.resolve();
  if (!hydratePromise) hydratePromise = hydrate();
  return hydratePromise;
}

// Best-effort module-load hydration. Errors are swallowed — readers will see
// an empty cache until hydration succeeds.
if (typeof window !== "undefined") {
  void whenSessionKeysHydrated();
}

function readStored(owner: PublicKey): StoredSessionKey | null {
  return memoryCache.get(owner.toBase58()) ?? null;
}

function writeStored(entry: StoredSessionKey): void {
  memoryCache.set(entry.ownerBase58, entry);
  void idbPut(entry);
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(lsKey(entry.ownerBase58), JSON.stringify(entry));
    } catch {
      /* quota — IDB is the source of truth */
    }
  }
  notifyChange();
}

export function clearStoredSessionKey(owner: PublicKey): void {
  const ownerB58 = owner.toBase58();
  memoryCache.delete(ownerB58);
  void idbDelete(ownerB58);
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(lsKey(ownerB58));
  }
  notifyChange();
}

export function getActiveSessionKey(owner: PublicKey): ActiveSessionKey | null {
  const entry = readStored(owner);
  if (!entry) return null;
  const nowSec = Math.floor(Date.now() / 1000);
  if (nowSec + EXPIRY_GRACE_SECS >= entry.expiresAt) {
    clearStoredSessionKey(owner);
    return null;
  }
  const secret = Uint8Array.from(atob(entry.delegateSecretBase64), (c) => c.charCodeAt(0));
  return {
    keypair: Keypair.fromSecretKey(secret),
    sessionKeyPda: deriveSessionKeyPda(owner),
    expiresAt: entry.expiresAt,
  };
}

/**
 * One-popup delegation: bundle the SystemTransfer (top-up) and
 * `koth_delegate_session_key` ix into a single signed tx. Persists the
 * ephemeral secret on success.
 */
export async function delegateSessionKey(
  connection: Connection,
  wallet: WalletContextState,
  durationSecs: number = DEFAULT_DURATION_SECS,
): Promise<ActiveSessionKey> {
  if (!wallet.publicKey || !wallet.signTransaction) {
    throw new Error("wallet not connected");
  }
  const owner = wallet.publicKey;
  const delegateKp = Keypair.generate();

  const program = buildWalletProgram(connection, wallet);
  const methods = program.methods as unknown as KothMethods;

  const delegateIx = await methods
    .kothDelegateSessionKey(delegateKp.publicKey, new BN(durationSecs))
    .accounts({
      sessionKey: deriveSessionKeyPda(owner, program.programId),
      owner,
      systemProgram: SystemProgram.programId,
    })
    .instruction()
    .then((ix: unknown) => ix as Awaited<ReturnType<typeof SystemProgram.transfer>>);

  const topUpIx = SystemProgram.transfer({
    fromPubkey: owner,
    toPubkey: delegateKp.publicKey,
    lamports: TOP_UP_LAMPORTS,
  });

  const { blockhash, lastValidBlockHeight } =
    await connection.getLatestBlockhash("confirmed");
  const tx = new Transaction({
    feePayer: owner,
    blockhash,
    lastValidBlockHeight,
  }).add(topUpIx, delegateIx as unknown as Parameters<Transaction["add"]>[0]);
  const signed = await wallet.signTransaction(tx);
  const sig = await connection.sendRawTransaction(signed.serialize(), {
    skipPreflight: false,
  });
  await connection.confirmTransaction(
    { signature: sig, blockhash, lastValidBlockHeight },
    "confirmed",
  );

  const expiresAt = Math.floor(Date.now() / 1000) + durationSecs;
  const secretB64 = btoa(
    String.fromCharCode(...Array.from(delegateKp.secretKey)),
  );
  writeStored({
    ownerBase58: owner.toBase58(),
    delegateSecretBase64: secretB64,
    delegateBase58: delegateKp.publicKey.toBase58(),
    expiresAt,
  });

  return {
    keypair: delegateKp,
    sessionKeyPda: deriveSessionKeyPda(owner, program.programId),
    expiresAt,
  };
}

/**
 * On-chain revoke + local clear. Safe to call even if no active key exists —
 * the on-chain step is skipped when nothing is stored.
 */
export async function revokeSessionKey(
  connection: Connection,
  wallet: WalletContextState,
): Promise<string | null> {
  if (!wallet.publicKey) throw new Error("wallet not connected");
  const owner = wallet.publicKey;
  const stored = readStored(owner);
  clearStoredSessionKey(owner);
  if (!stored) return null;

  const program = buildWalletProgram(connection, wallet);
  const methods = program.methods as unknown as KothMethods;
  try {
    const sig = await methods
      .kothRevokeSessionKey()
      .accounts({
        sessionKey: deriveSessionKeyPda(owner, program.programId),
        owner,
      })
      .rpc();
    return sig;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    // PDA may already be closed (expired + reaped); treat as success.
    if (/AccountNotInitialized|account .* not found/i.test(msg)) return null;
    throw e;
  }
}

/**
 * Build a write-capable Anchor `Program` whose signer is a raw `Keypair`
 * instead of a wallet-adapter wallet. Used to call `koth_touch_delegated`
 * with the ephemeral session key — no popup.
 */
export function buildSessionKeyProgram(
  connection: Connection,
  keypair: Keypair,
): Program {
  const provider = new AnchorProvider(
    connection,
    new KeypairWallet(keypair),
    { commitment: "confirmed", preflightCommitment: "confirmed" },
  );
  return new Program(IDL as unknown as AnchorIdl, provider);
}

class KeypairWallet {
  constructor(private readonly keypair: Keypair) {}
  get publicKey(): PublicKey {
    return this.keypair.publicKey;
  }
  async signTransaction<T extends Transaction | VersionedTransaction>(
    tx: T,
  ): Promise<T> {
    if (tx instanceof Transaction) {
      tx.partialSign(this.keypair);
    } else {
      tx.sign([this.keypair]);
    }
    return tx;
  }
  async signAllTransactions<T extends Transaction | VersionedTransaction>(
    txs: T[],
  ): Promise<T[]> {
    for (const tx of txs) await this.signTransaction(tx);
    return txs;
  }
}

// Re-export program id for symmetry with anchor.ts.
export const PROGRAM_ID_PK = new PublicKey(PROGRAM_ID);
